import { literalListSerialiser } from "hooks/serialisers/literalList";
import { useQueryParamState } from "hooks/useQueryParamState";
import { createContext, ReactNode, use, useCallback, useMemo } from "react";
import {
  HEAT_FILTER_FIELDS,
  HeatFilterField,
  HeatRowFilters,
  heatFilterOptions,
  isColumnFiltered,
  NO_HEAT_ROW_FILTERS,
} from "../heatsRowFilter";
import { useHeatsData } from "./HeatsDataContext";

export interface HeatsRowFilterContextType {
  options: HeatRowFilters;
  selection: HeatRowFilters;
  setFieldValues: (field: HeatFilterField, values: string[]) => void;
  isFiltering: boolean;
}

const HeatsRowFilterContext = createContext<
  HeatsRowFilterContextType | undefined
>(undefined);

const NO_SELECTION: string[] = [];

const useFieldSelection = (
  paramName: string,
  permitted: string[]
): [string[], (values: string[]) => void] => {
  const serialiser = useMemo(
    () => literalListSerialiser(permitted),
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- permitted is a fresh array each render; key on its contents to keep the serialiser identity stable
    [JSON.stringify(permitted)]
  );

  return useQueryParamState<string[], never>(
    paramName,
    serialiser,
    NO_SELECTION
  );
};

export const HeatsRowFilterProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { data } = useHeatsData();

  // One element type whatever the data holds. Swapping the provider out while
  // the heats load would unmount the whole tab below it.
  const options = useMemo(
    () =>
      data === undefined ? NO_HEAT_ROW_FILTERS : heatFilterOptions(data.series),
    [data]
  );

  const [eafs, setEafs] = useFieldSelection("eaf", options.eaf);
  const [crews, setCrews] = useFieldSelection("crew", options.crew_id);
  const [recipes, setRecipes] = useFieldSelection(
    "recipe",
    options.recipe_name
  );

  const setFieldValues = useCallback(
    (field: HeatFilterField, values: string[]) => {
      const setters: Record<HeatFilterField, (next: string[]) => void> = {
        eaf: setEafs,
        crew_id: setCrews,
        recipe_name: setRecipes,
      };
      setters[field](values);
    },
    [setEafs, setCrews, setRecipes]
  );

  const value = useMemo<HeatsRowFilterContextType>(() => {
    const selection: HeatRowFilters = {
      eaf: eafs,
      crew_id: crews,
      recipe_name: recipes,
    };
    return {
      options,
      selection,
      setFieldValues,
      isFiltering: HEAT_FILTER_FIELDS.some((field) =>
        isColumnFiltered(selection[field])
      ),
    };
  }, [options, eafs, crews, recipes, setFieldValues]);

  return (
    <HeatsRowFilterContext value={value}>{children}</HeatsRowFilterContext>
  );
};

export const useHeatsRowFilter = (): HeatsRowFilterContextType => {
  const context = use(HeatsRowFilterContext);
  if (context === undefined) {
    throw new Error(
      "useHeatsRowFilter must be used within a HeatsRowFilterProvider"
    );
  }
  return context;
};
